// Lib
import React, { RefObject } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect, ConnectedProps } from 'react-redux';
import { debounce, defer } from 'lodash/fp';
import classNames from 'classnames';

// Utils
import doesEditorJsonHaveText from '../../../common/tiptap/utils/jsonContentUtils/doesEditorJsonHaveText';
import { noLongerEditingElement } from '../../components/editor/milanoteEditor/editorPropComparisons';

// Selectors
import {
    getCommentTriggerOrigin,
    getLocalCommentInputIsEditing,
    getLocalCommentInputText,
} from './store/commentsInputSelector';
import { getCurrentUser } from '../../user/currentUserSelector';

// Components
import AnchorButton from '../../components/buttons/AnchorButton';
import CommentEditor from './commentEditor/CommentEditor';
import CommentLine from './CommentLine';
import { GroupTranslatorProvider, useGroupTranslator } from '../../components/i18n/GroupTranslationContext';

// Actions
import {
    startEditingComment,
    stopEditingComment,
    preCommitComment,
    clearPreCommitComment,
} from './store/localCommentActions';
import { commentsTypingPing, commentsTypingStop } from './store/commentThreadActivityActions';
import { finishEditingElement, removeSelectedElements } from '../selection/selectionActions';

// Constants
import { TIMES } from '../../../common/utils/timeUtil';
import { ElementType } from '../../../common/elements/elementTypes';
import { CommentTriggerOrigin } from '../../../common/comments/commentConstants';
import { TiptapContent } from '../../../common/tiptap/tiptapTypes';
import { ImMap, ImmutableMap } from '../../../common/utils/immutableHelper';
import { DRAG_PREVIEW_ID } from '../../reducers/draggingConstants';
import { SaveTiptapEditorContentFunction } from '../../../common/tiptap/extensions/externalStoreSync/externalStoreSynchroniserTypes';
import { getNewTransactionId } from '../../utils/undoRedo/undoRedoTransactionManager';

// Styles
import './CommentForm.scss';

export const getCommentFormEditorId = (threadId: string) => `${threadId}-${ElementType.COMMENT_THREAD_TYPE}`;
export const getCommentFormEditorKey = () => ElementType.COMMENT_THREAD_TYPE;

const ExtraPadding = () => (
    <div className="CommentForm">
        <div className="extra-padding" />
    </div>
);

// @ts-ignore TS doesn't understand lodash/fp's convert
const debounceNotFixed = debounce.convert({ fixed: false });

const currentUserIsCreator = ({ creatorId, currentUserId }: CommentFormProps) => creatorId === currentUserId;

const noCommentsAndCurrentUserIsNotCreator = (props: CommentFormProps) =>
    !props.commentList?.length && !currentUserIsCreator(props);

const noCommentsAndCurrentUserIsCreator = (props: CommentFormProps) =>
    !props.commentList?.length && currentUserIsCreator(props);

// Define an interface for the props that will be passed to mapStateToProps
interface CommentFormOwnProps {
    creatorId: string;
    elementId: string;
    threadId: string;

    currentEditorKey: string;
    currentEditorId: string;
    currentUserId?: string;

    isSingleSelected: boolean;
    isEditing?: boolean;
    isEditable?: boolean;
    isPresentational?: boolean;
    hideEditing?: boolean;
    inTrash?: boolean;
    spellCheck: boolean;
    isCollapsedThread?: boolean;
    isDuplicating?: boolean;

    commentList?: any[]; // TODO: Replace with Comment[]
    activeUsers?: Map<string, any>; // TODO: Replace with ActiveUsers type

    onSubmit: (textContent: TiptapContent, transactionId: number) => void;
    shouldDeselectAfterSubmit?: boolean;
    startEditing?: (options: { editorId: string; editorKey: string; editorFocusRect?: DOMRect }) => void;
    saveContent: SaveTiptapEditorContentFunction;
    moveElementsToTrash: () => void;
    onEmptyBackspace?: () => void;
}

const mapStateToProps = createStructuredSelector<
    ImmutableMap,
    CommentFormOwnProps,
    {
        currentUser: ReturnType<typeof getCurrentUser>;
        isReplying: boolean;
        localText: ImMap<TiptapContent> | null;
        triggerOrigin: string | null;
    }
>({
    currentUser: getCurrentUser,
    isReplying: (state: ImmutableMap, props: CommentFormOwnProps) =>
        getLocalCommentInputIsEditing(state, { _id: props.threadId }),
    localText: (state: ImmutableMap, props: CommentFormOwnProps) =>
        getLocalCommentInputText(state, { _id: props.threadId }),
    triggerOrigin: (state: ImmutableMap, props: CommentFormOwnProps) =>
        getCommentTriggerOrigin(state, { _id: props.threadId }),
});

const mapDispatchToProps = (dispatch: Function) => ({
    dispatchStopEditingComment: ({ _id }: { _id: string }) => dispatch(stopEditingComment({ _id })),
    dispatchStartEditingComment: ({
        _id,
        elementId,
        triggerOrigin,
    }: {
        _id: string;
        elementId: string;
        triggerOrigin: string | null;
    }) => dispatch(startEditingComment({ _id, elementId, triggerOrigin })),
    dispatchPreCommitContent: ({ _id, text }: { _id: string; text: TiptapContent }) =>
        dispatch(preCommitComment({ _id, text })),
    dispatchClearPreCommitContent: ({ _id }: { _id: string }) => dispatch(clearPreCommitComment({ _id })),
    dispatchFinishEditingElement: () => dispatch(finishEditingElement()),
    dispatchDeselectCommentThread: ({ elementId, transactionId }: { elementId: string; transactionId: number }) =>
        dispatch(removeSelectedElements({ ids: [elementId], transactionId })),

    dispatchCommentsTypingPing: ({ threadId, elementId }: { threadId: string; elementId: string }) =>
        dispatch(commentsTypingPing({ threadId, elementId })),
    dispatchCommentsTypingStop: ({ threadId, elementId }: { threadId: string; elementId: string }) =>
        dispatch(commentsTypingStop({ threadId, elementId })),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

type CommentFormProps = PropsFromRedux & CommentFormOwnProps;

const ReplyButtonText: React.FC = () => {
    const translate = useGroupTranslator<'elements', 'elementTypes.comment'>();
    return <>{translate('replyButton')}</>;
};

// Wrapper component to pass translated placeholder to CommentEditor
const TranslatedCommentEditor: React.FC<{
    isUpdating: boolean;
    onActivity: () => void;
    editorId: string;
    editorKey: string;
    currentEditorKey: string;
    currentEditorId: string;
    textContent: ImMap<TiptapContent> | null;
    saveContent: SaveTiptapEditorContentFunction;
    onSubmit: (text: TiptapContent) => void;
    onEmptyBackspace: () => void;
    startEditing: (options?: { editorFocusRect?: DOMRect }) => void;
    isEditing?: boolean;
    isEditable?: boolean;
    isSingleSelected: boolean;
    spellCheck: boolean;
}> = (props) => {
    const translate = useGroupTranslator<'elements', 'elementTypes.comment'>();
    return <CommentEditor {...props} placeholder={translate('form.placeholder')} />;
};

class CommentForm extends React.Component<CommentFormProps> {
    private readonly replyButtonRef: RefObject<HTMLDivElement>;
    private stopTypingTimeout: ReturnType<typeof setTimeout> | null = null;

    constructor(props: CommentFormProps) {
        super(props);
        this.replyButtonRef = React.createRef();
    }

    componentDidUpdate(prevProps: CommentFormProps) {
        // Need to defer because it requires the "localText" to be set, which is performed by
        // the "this.saveContent" function which is executed after componentDidUpdate
        if (
            this.props.isReplying &&
            noLongerEditingElement(prevProps, this.props) &&
            prevProps.triggerOrigin !== CommentTriggerOrigin.TOOLBAR
        )
            defer(this.stopEditingIfEmpty);
    }

    componentDidMount() {
        // on mount, if the thread is inside a collapsable popup,
        // and the current user created the thread OR the user is replying,
        // start editing
        if (this.props.isCollapsedThread && !this.props.isDuplicating) {
            if (
                noCommentsAndCurrentUserIsCreator(this.props) ||
                this.props.triggerOrigin === CommentTriggerOrigin.TOOLBAR
            ) {
                this.startEditing();
            }
        }
    }

    componentWillUnmount() {
        this.clearAllTimeouts();
    }

    onAddCommentClick = () => {
        if (this.props.isDuplicating) return;

        this.startEditing();
    };

    onEmptyBackspace = () => {
        const { threadId, dispatchStopEditingComment, dispatchFinishEditingElement, moveElementsToTrash } = this.props;

        // Return element to non editing state
        dispatchStopEditingComment({ _id: threadId });
        dispatchFinishEditingElement();
        this.stopTypingActivity();

        if (noCommentsAndCurrentUserIsCreator(this.props)) moveElementsToTrash();
    };

    onSubmit = (text: TiptapContent) => {
        const {
            onSubmit,
            elementId,
            dispatchFinishEditingElement,
            dispatchDeselectCommentThread,
            dispatchClearPreCommitContent,
            shouldDeselectAfterSubmit = true,
        } = this.props;

        this.stopTypingActivity();
        dispatchFinishEditingElement();
        const transactionId = getNewTransactionId();
        onSubmit(text, transactionId);
        if (shouldDeselectAfterSubmit) {
            dispatchDeselectCommentThread({ elementId, transactionId });
        }

        defer(() => dispatchClearPreCommitContent({ _id: elementId }));
    };

    startEditing = ({ editorFocusRect }: { editorFocusRect?: DOMRect } = {}) => {
        const { startEditing, dispatchStartEditingComment, threadId, elementId, triggerOrigin } = this.props;

        if (!startEditing) return;

        dispatchStartEditingComment({ _id: threadId, elementId, triggerOrigin });

        const replyButtonClientRect = this.replyButtonRef.current?.getBoundingClientRect();

        // This avoids a race condition on iOS where the input sometimes doesn't receive focus.
        requestAnimationFrame(() => {
            startEditing({
                editorId: getCommentFormEditorId(threadId),
                editorKey: getCommentFormEditorKey(),
                editorFocusRect: editorFocusRect || replyButtonClientRect,
            });
        });
    };

    stopEditingIfEmpty = () => {
        const { threadId, dispatchStopEditingComment, isReplying, localText } = this.props;
        if (isReplying && !doesEditorJsonHaveText(localText)) dispatchStopEditingComment({ _id: threadId });
    };

    saveContent = (text: TiptapContent) => {
        const { dispatchPreCommitContent, threadId, isReplying } = this.props;

        const showEditor = isReplying || noCommentsAndCurrentUserIsCreator(this.props);

        // If the editor is not visible, don't pre-commit the content, it's been submitted
        if (!showEditor) return;

        dispatchPreCommitContent({ text, _id: threadId });
    };

    stopTypingActivity = () => {
        const { dispatchCommentsTypingStop, threadId, elementId } = this.props;
        this.clearAllTimeouts();
        dispatchCommentsTypingStop({ threadId, elementId });
    };

    clearAllTimeouts = () => {
        if (this.stopTypingTimeout) clearTimeout(this.stopTypingTimeout);
        this.broadcastActivity.cancel();
    };

    broadcastActivity = debounceNotFixed(
        TIMES.SECOND,
        () => {
            if (this.stopTypingTimeout) clearTimeout(this.stopTypingTimeout);

            const { dispatchCommentsTypingPing, threadId, elementId } = this.props;

            // don't attempt to start editing a comment when dragging a new one out of the toolbar
            if (threadId === DRAG_PREVIEW_ID || !threadId) return;

            dispatchCommentsTypingPing({ threadId, elementId });
            this.stopTypingTimeout = setTimeout(this.stopTypingActivity, 5 * TIMES.SECOND);
        },
        { leading: true, trailing: false, maxWait: 3 * TIMES.SECOND },
    );

    render() {
        const {
            threadId,
            currentEditorId,
            currentEditorKey,

            localText,
            currentUser,

            isReplying,
            isEditing,
            isEditable,
            isPresentational,
            isSingleSelected,
            inTrash,
            isDuplicating,
            spellCheck,

            activeUsers,
            hideEditing,
        } = this.props;

        if (noCommentsAndCurrentUserIsNotCreator(this.props)) return null;
        if (inTrash) return <ExtraPadding />;

        if (!isEditable && !isPresentational && !hideEditing) return null;

        const showEditor = isReplying || noCommentsAndCurrentUserIsCreator(this.props);

        const showingActivityIndicator = activeUsers && activeUsers.size;

        const classes = classNames('CommentForm', {
            'showing-activity': showingActivityIndicator,
            replying: showEditor,
            hide: hideEditing,
        });

        return (
            <GroupTranslatorProvider group="elements" keyPrefix="elementTypes.comment">
                {!showEditor ? (
                    <div className={classes} ref={this.replyButtonRef}>
                        <AnchorButton
                            className={classNames('reply-button', { disabled: isDuplicating })}
                            onClickFn={this.onAddCommentClick}
                        >
                            <ReplyButtonText />
                        </AnchorButton>
                    </div>
                ) : (
                    <CommentLine className={classes} user={currentUser}>
                        <TranslatedCommentEditor
                            isUpdating
                            onActivity={this.broadcastActivity}
                            editorId={getCommentFormEditorId(threadId)}
                            editorKey={getCommentFormEditorKey()}
                            currentEditorKey={currentEditorKey}
                            currentEditorId={currentEditorId}
                            textContent={localText}
                            saveContent={this.saveContent}
                            onSubmit={this.onSubmit}
                            onEmptyBackspace={this.onEmptyBackspace}
                            startEditing={this.startEditing}
                            isEditing={isEditing}
                            isEditable={isEditable}
                            isSingleSelected={isSingleSelected}
                            spellCheck={spellCheck}
                        />
                    </CommentLine>
                )}
            </GroupTranslatorProvider>
        );
    }
}

export default connector(CommentForm);
